import React, { useEffect, useRef } from "react";

/**
 * Reusable confirmation dialog. Replaces window.confirm so the copy can be
 * Bulgarian, the primary action can show a busy state, and the surface stays
 * viewport-safe on the narrow breakpoints fixed in Phase 2C.1.
 *
 * Duplicate-click protection lives here: while `submitting` is true the primary
 * button is disabled AND onConfirm is not forwarded again.
 */
export const ConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  submitting = false,
  submittingLabel,
  error,
  tone = "primary",
  testId = "confirm-dialog",
}) => {
  // React state lags one render behind, so `submitting` alone cannot stop clicks
  // dispatched within the same event-loop tick. This ref closes synchronously.
  // It gates the HANDLER only — never the `disabled` attribute, because mutating
  // a ref does not re-render, which would leave the button stuck after an error.
  const inFlight = useRef(false);

  useEffect(() => {
    if (!submitting) inFlight.current = false;
  }, [submitting]);

  useEffect(() => {
    if (!open) inFlight.current = false;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const titleId = `${testId}-title`;

  const handleConfirm = () => {
    if (inFlight.current || submitting) return;
    inFlight.current = true;
    onConfirm();
  };

  return (
    <div className="qw-modal-backdrop">
      <div
        className="qw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
      >
        <div className="h" id={titleId} data-testid={`${testId}-title`}>{title}</div>
        <div className="body">
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }} data-testid={`${testId}-body`}>
            {body}
          </p>
          {error && (
            <div className="qw-error" style={{ marginTop: 10 }} data-testid={`${testId}-error`}>
              {error}
            </div>
          )}
        </div>
        <div className="footer">
          <button
            type="button"
            className="qw-btn ghost"
            onClick={onCancel}
            disabled={submitting}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`qw-btn ${tone}`}
            onClick={handleConfirm}
            disabled={submitting}
            data-testid={`${testId}-confirm`}
          >
            {submitting ? (submittingLabel || confirmLabel) : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
